import { get } from "lodash";
import React, { PureComponent, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Header, Container, Tab } from "semantic-ui-react";

import UserDetailsTab from "./UserDetailsTab.jsx";
import EmailTab from "./EmailTab.jsx";
import SecurityTab from "./SecurityTab.jsx";
import OrganizationTab from "./OrganizationTab.jsx";
import SegmentMessage from "../../components/forms/messages/SegmentMessage.jsx";
import SimplePreloader from "../../components/preloaders/SimplePreloader.jsx";
import UnauthorisedFeature from "../../components/auth/UnauthorisedFeature.jsx";
import Entitiy404 from "../../components/entity404/Entitiy404.jsx";

import { AT_TYPES } from "../../constants/entity.js";
import { OPERATIONS } from "../../constants/operations.js";

import { openHelpWidget } from "../../state/reducers/helpWidget/helpWidgetActions.js";
import {
  getAccount,
  updateAccount
} from "../../state/reducers/account/accountActions.js";

import endpointSelector from "../../state/reducers/endpoints/endpointSelector.js";
import accountSelectors from "../../state/reducers/account/accountSelectors.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class Account extends PureComponent {
  static propTypes = {
    pendingEmailVerification: PropTypes.bool,
    user: PropTypes.object.isRequired,
    isSuccess: PropTypes.bool.isRequired,
    isFetching: PropTypes.bool.isRequired,
    getAccount: PropTypes.func.isRequired,
    updateAccount: PropTypes.func.isRequired,
    copy: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);

    this.tabIndex = ["profile", "email", "security", "organization"];

    this.state = {
      showSubmitMessages: false,
      activeIndex: this.indexLookup()
    };
  }

  componentDidMount = () => {
    const { listAccountAvailability, getAccount } = this.props;
    if (listAccountAvailability.available) {
      getAccount();
    }
  };

  componentDidUpdate(prevProps) {
    if (prevProps.match !== this.props.match) {
      this.setState({ activeIndex: this.indexLookup(), newTabURL: null });
    }
  }

  // -- TAB MANAGEMENT -- //

  indexLookup = () => {
    const tab = get(this.props, "match.params.tab", null);
    const index = this.tabIndex.indexOf(tab);
    return index > 0 ? index : 0;
  };

  onTabChange = (evt, { activeIndex }) => {
    const newTabURL = `/account/${this.tabIndex[activeIndex]}`;

    this.setState({ showSubmitMessages: false });

    // using this approach to as <Redirect /> throws a warning
    this.props.history.push(newTabURL);
  };

  // -- HANDLERS -- //

  onSubmit = values => {
    const { user } = this.props;

    const data = {
      ...values,
      "@id": user["@id"]
    };

    // format timezone
    if (values.timezone) {
      data.timezone = values.timezone["@id"];
    }

    this.setState({ showSubmitMessages: true });

    this.props.updateAccount(data);
  };

  requestLimitHandler = () => {
    const { openHelpWidget, copy } = this.props;

    const data = {
      subject: copy("help_widget.limits.subject"),
      description: copy("help_widget.limits.description")
    };

    openHelpWidget(data);
  };

  // -- RENDER -- //

  renderNotifications = () => {
    const { pendingEmailVerification, isSuccess, copy } = this.props;
    const { showSubmitMessages } = this.state;

    return (
      <Fragment>
        {pendingEmailVerification && (
          <SegmentMessage
            title={copy("account.email.pending_notification.title")}
            message={copy("account.email.pending_notification.message")}
            info={true}
            dismissable={false}
            timed={false}
          />
        )}
        {isSuccess && showSubmitMessages && (
          <SegmentMessage
            title={copy("forms.saved_message.title")}
            message={copy("forms.saved_message.message")}
            positive={true}
            dismissable={true}
            timed={false}
          />
        )}
      </Fragment>
    );
  };

  renderPaneContent = (ContentComponent, key) => {
    const { user, isSuccess, isFetching } = this.props;

    return (
      <Tab.Pane key={key}>
        <ContentComponent
          data={user}
          isSuccess={isSuccess}
          isFetching={isFetching}
          onSubmit={this.onSubmit}
        />
      </Tab.Pane>
    );
  };

  renderPanes = () => {
    const { user, copy } = this.props;

    if (!user) return null;

    const result = [
      {
        menuItem: copy("account.tabs.user_details"),
        render: () => this.renderPaneContent(UserDetailsTab, "details-tab")
      },
      {
        menuItem: copy("account.tabs.email"),
        render: () => this.renderPaneContent(EmailTab, "email-tab")
      },
      {
        menuItem: copy("account.tabs.security"),
        render: () => this.renderPaneContent(SecurityTab, "security-tab")
      }
    ];

    if (user.businessModel > 0) {
      // OrganizationTab

      result.push({
        menuItem: copy("account.tabs.organization"),
        render: () => (
          <Tab.Pane key="organization-tab">
            <OrganizationTab
              user={user}
              onSubmit={this.onSubmit}
              requestLimitHandler={this.requestLimitHandler}
            />
          </Tab.Pane>
        )
      });
    }

    return result;
  };

  render() {
    const {
      user,
      isFetching,
      listAccountAvailability: { permitted, enabled },
      copy
    } = this.props;
    const { activeIndex } = this.state;

    if (!permitted) {
      return <UnauthorisedFeature title={copy("account.title")} />;
    }

    // early return if project isn't ready to render
    // if (isFetching) {
    //   return <SimplePreloader />;
    // }

    if (!enabled || !user || !user["@id"]) {
      return <Entitiy404 id={atId} type={AT_TYPES.USER} />;
    }

    return (
      <Fragment>
        <Container className="main-responsive">
          <Header as="h3">{copy("account.title")}</Header>
          {this.renderNotifications()}
          <Tab
            panes={this.renderPanes()}
            activeIndex={activeIndex}
            renderActiveOnly={true}
            onTabChange={this.onTabChange}
          />
        </Container>
        {isFetching && <SimplePreloader />}
      </Fragment>
    );
  }
}

const mapStateToProps = state => ({
  user: accountSelectors.selectAccount(state),
  pendingEmailVerification: state.account.data.pendingEmailVerification,
  isSuccess: state.account.isSuccess,
  isFetching: state.account.isFetching,
  listAccountAvailability: endpointSelector.selectOperationAvailability(state)(
    AT_TYPES.USER,
    OPERATIONS.LIST
  ),
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  openHelpWidget: data => dispatch(openHelpWidget(data)),
  getAccount: () => dispatch(getAccount()),
  updateAccount: data => dispatch(updateAccount(data))
});

export default connect(mapStateToProps, mapDispatchToProps)(Account);
